import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { toastr } from 'react-redux-toastr';
import { withApollo } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import gql from 'graphql-tag';
import s from './ManageNotificationsForm.css';
import NotificationBannerUpload from './NotificationBannerUpload';
import Select from 'react-select';

const FCM_TITLE_LIMIT = 65;
const FCM_BODY_LIMIT = 240;

const USER_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: 'Customer' },
  { value: '2', label: 'Driver' },
  { value: '3', label: 'Shop' },
];

// Convert UTC ISO string from DB to datetime-local input value (local time)
function utcToLocalInput(utcString) {
  if (!utcString) return '';
  const d = new Date(utcString);
  // datetime-local format: YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() + '-' +
    pad(d.getMonth() + 1) + '-' +
    pad(d.getDate()) + 'T' +
    pad(d.getHours()) + ':' +
    pad(d.getMinutes())
  );
}

class EditScheduledNotificationModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      title: '',
      body: '',
      scheduledAt: '',
      userType: '',
      imageUrl: '',
      locationIds: [],
      isPersonalizedNotification: false,
      saving: false,
      errors: {},
    };
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { notification } = this.props;
    if (notification && notification !== prevProps.notification) {
      this.setState({
        title: notification.title || '',
        body: notification.body || '',
        scheduledAt: utcToLocalInput(notification.scheduledAt),
        userType: notification.userType != null ? String(notification.userType) : '',
        imageUrl: notification.imageUrl || '',
        locationIds: notification.locationIds ? JSON.parse(notification.locationIds) : [],
        isPersonalizedNotification: notification.isPersonalizedNotification || false,
        errors: {},
      });
    }
  }

  validate() {
    const { title, body, scheduledAt } = this.state;
    const errors = {};
    if (!title.trim()) errors.title = 'Title is required.';
    else if (title.length > FCM_TITLE_LIMIT) errors.title = `Max ${FCM_TITLE_LIMIT} characters.`;

    if (!body.trim()) errors.body = 'Body is required.';
    else if (body.length > FCM_BODY_LIMIT) errors.body = `Max ${FCM_BODY_LIMIT} characters.`;

    if (!scheduledAt) {
      errors.scheduledAt = 'Scheduled time is required.';
    }
    return errors;
  }

  async handleSave() {
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const { title, body, scheduledAt, userType, imageUrl, isPersonalizedNotification } = this.state;
    const { notification, onClose, client } = this.props;

    this.setState({ saving: true });
    try {
      const utcDate = new Date(scheduledAt).toISOString();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const { data } = await client.mutate({
        mutation: gql`mutation updateScheduledNotification(
          $id: Int, $title: String, $body: String, $scheduledAt: String,
          $timezone: String, $userType: Int, $imageUrl: String,
          $isPersonalizedNotification: Boolean, $locationIds: [Int]
        ) {
          updateScheduledNotification(
            id: $id, title: $title, body: $body, scheduledAt: $scheduledAt,
            timezone: $timezone, userType: $userType, imageUrl: $imageUrl,
            isPersonalizedNotification: $isPersonalizedNotification,
            locationIds: $locationIds
          ) { status errorMessage }
        }`,
        variables: {
          id: notification.id,
          title,
          body,
          scheduledAt: utcDate,
          timezone,
          userType: userType ? Number(userType) : null,
          imageUrl: imageUrl || null,
          isPersonalizedNotification,
          locationIds: this.state.locationIds.map(id => Number(id)),
        },
        refetchQueries: ['adminGetScheduledNotification'],
      });

      if (data?.updateScheduledNotification?.status === 200) {
        toastr.success('Updated', 'Scheduled notification updated successfully.');
        onClose && onClose();
      } else {
        toastr.error('Error', data?.updateScheduledNotification?.errorMessage || 'Failed to update.');
      }
    } catch (e) {
      toastr.error('Error', e.message || 'Something went wrong.');
    } finally {
      this.setState({ saving: false });
    }
  }

  render() {
    const { show, onClose } = this.props;
    const { title, body, scheduledAt, userType, imageUrl, isPersonalizedNotification, saving, errors } = this.state;

    const titleLen = title.length;
    const bodyLen = body.length;

    return (
      <Modal show={show} onHide={onClose} enforceFocus={false} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Scheduled Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Target */}
            <Form.Group className="mb-3">
              <Form.Label>Target Recipients</Form.Label>
              <Form.Control
                as="select"
                value={userType}
                onChange={(e) => this.setState({ userType: e.target.value })}
              >
                {USER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Control>
            </Form.Group>

            {/* Area */}
            <Form.Group className="mb-3">
              <Form.Label>Target Area (Geofencing)</Form.Label>
              <Select
                isMulti
                value={(this.props.locations || [])
                  .filter(loc => this.state.locationIds.includes(String(loc.id)))
                  .map(loc => ({ value: String(loc.id), label: `📍 ${loc.locationName}` }))}
                onChange={(opts) => this.setState({ locationIds: opts ? opts.map(o => o.value) : [] })}
                options={(this.props.locations || []).map(loc => ({ value: String(loc.id), label: `📍 ${loc.locationName}` }))}
                placeholder="Select areas..."
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    borderColor: '#dee2e6',
                    minHeight: '38px',
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '14px',
                    backgroundColor: state.isSelected ? '#E62529' : state.isFocused ? 'rgba(230, 37, 41, 0.05)' : 'transparent',
                    '&:active': { backgroundColor: '#E62529' }
                  })
                }}
              />
              <Form.Text className="text-muted">Filter recipients based on current coordinates.</Form.Text>
            </Form.Group>

            {/* Title */}
            <Form.Group className="mb-3">
              <div className={s.fieldWithCounter}>
                <Form.Label>Notification Title</Form.Label>
                <span className={cx(s.charCounter, titleLen > FCM_TITLE_LIMIT && s.charCounterOver)}>
                  {titleLen}/{FCM_TITLE_LIMIT}
                </span>
              </div>
              <Form.Control
                type="text"
                value={title}
                maxLength={FCM_TITLE_LIMIT}
                onChange={(e) => this.setState({ title: e.target.value, errors: { ...errors, title: undefined } })}
                isInvalid={!!errors.title}
              />
              {errors.title && <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>}
            </Form.Group>

            {/* Body */}
            <Form.Group className="mb-3">
              <div className={s.fieldWithCounter}>
                <Form.Label>Notification Body</Form.Label>
                <span className={cx(s.charCounter, bodyLen > FCM_BODY_LIMIT && s.charCounterOver)}>
                  {bodyLen}/{FCM_BODY_LIMIT}
                </span>
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                value={body}
                maxLength={FCM_BODY_LIMIT}
                onChange={(e) => this.setState({ body: e.target.value, errors: { ...errors, body: undefined } })}
                isInvalid={!!errors.body}
              />
              {errors.body && <Form.Control.Feedback type="invalid">{errors.body}</Form.Control.Feedback>}
            </Form.Group>

            {/* Image Banner */}
            <Form.Group className="mb-3">
              <Form.Label>Drop the new banner here <span className="text-muted fw-normal">(optional)</span></Form.Label>
              <NotificationBannerUpload
                value={imageUrl}
                onChange={(url) => this.setState({ imageUrl: url || '' })}
                disabled={saving}
              />
            </Form.Group>

            {/* Personalized notification toggle */}
            <Form.Group className="mb-3">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f7f8fa', border: '1px solid #e8eaed', borderRadius: 10, padding: '12px 16px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Personalized Notification</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Use {'{{'+'firstName}}'} or {'{{'+'lastName}}'} in title/body
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="edit-personalized-switch"
                  checked={isPersonalizedNotification}
                  onChange={(e) => this.setState({ isPersonalizedNotification: e.target.checked })}
                  disabled={saving}
                />
              </div>
              {isPersonalizedNotification && (
                <div style={{ marginTop: 8, background: '#eef6ff', border: '1px solid #b3d7ff', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1a5fa8' }}>
                  Use <code>{'{{firstName}}'}</code> and <code>{'{{lastName}}'}</code> to personalize each message. Messages are sent individually per recipient.
                </div>
              )}
            </Form.Group>

            {/* Scheduled At */}
            <Form.Group className="mb-3">
              <Form.Label>Scheduled At (local time)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => this.setState({ scheduledAt: e.target.value, errors: { ...errors, scheduledAt: undefined } })}
                isInvalid={!!errors.scheduledAt}
              />
              {errors.scheduledAt && <Form.Control.Feedback type="invalid">{errors.scheduledAt}</Form.Control.Feedback>}
              <Form.Text className="text-muted">
                Time is converted to UTC before saving.
              </Form.Text>
            </Form.Group>

            {/* Preview */}
            <Row>
              <Col xs={12}>
                <div className={s.sectionHeading + ' mb-2'}>Preview</div>
                <div className={s.previewWrapper}>
                  <AndroidPreview title={title} body={body} imageUrl={imageUrl} />
                  <IphonePreview title={title} body={body} imageUrl={imageUrl} />
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={this.handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

// Inline previews (same as in ManageNotificationsForm)
const APP_ICON = <div style={{
  width: 32, height: 32, borderRadius: 8, background: '#E62529',
  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 14, fontWeight: 700, flexShrink: 0,
}}>A</div>;

const BANNER_STYLE = {
  width: '100%', maxHeight: 70, objectFit: 'cover', borderRadius: 6, marginTop: 8,
};

function AndroidPreview({ title, body, imageUrl }) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6, textAlign: 'center' }}>Android</div>
      <div style={{
        background: '#1a1a1a', borderRadius: 24, padding: '12px 0 6px', width: '100%',
        maxWidth: 280, margin: '0 auto',
      }}>
        <div style={{ height: 4, background: '#333', margin: '0 16px 8px' }} />
        <div style={{
          background: '#fff', margin: '0 8px', borderRadius: 12, padding: '10px 12px',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {APP_ICON}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>ASAP</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111', wordBreak: 'break-word' }}>
                {title || 'Notification title'}
              </div>
              <div style={{ fontSize: 12, color: '#555', marginTop: 2, wordBreak: 'break-word' }}>
                {body || 'Notification message will appear here.'}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#aaa', whiteSpace: 'nowrap' }}>now</div>
          </div>
          {imageUrl && <img src={imageUrl} alt="banner" style={BANNER_STYLE} />}
        </div>
        <div style={{ height: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 6 }}>
          <div style={{ width: 80, height: 4, background: '#444', borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}

function IphonePreview({ title, body, imageUrl }) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6, textAlign: 'center' }}>iPhone</div>
      <div style={{
        background: '#000', borderRadius: 44, padding: '8px 0 12px', width: '100%',
        maxWidth: 280, margin: '0 auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px 8px', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>9:41</span>
          <div style={{ width: 72, height: 20, background: '#111', borderRadius: 10 }} />
          <div style={{ width: 32 }} />
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.92)', margin: '0 10px', borderRadius: 16, padding: '10px 12px',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            {APP_ICON}
            <span style={{ fontSize: 11, fontWeight: 600, color: '#111' }}>ASAP</span>
            <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto' }}>now</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111', wordBreak: 'break-word' }}>
            {title || 'Notification title'}
          </div>
          <div style={{ fontSize: 12, color: '#555', marginTop: 2, wordBreak: 'break-word' }}>
            {body || 'Notification message will appear here.'}
          </div>
          {imageUrl && <img src={imageUrl} alt="banner" style={BANNER_STYLE} />}
        </div>
        <div style={{ height: 20, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 2 }}>
          <div style={{ width: 100, height: 4, background: '#444', borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}

export default compose(
  withStyles(s),
  withApollo,
)(EditScheduledNotificationModal);
